import React, { FC, SetStateAction, Dispatch, useState } from 'react';
import { Button, message, theme } from 'antd';
import { useUserContext } from '../../AppContext';
import useProjectId from '@launchnotes/common-hooks/useProjectId';
import { useImportFeedbackMutation, BatchFeedback } from '../../generated/graphql';
import { lnTokens } from '../../Router';
import { getFlatfileKey } from '@launchnotes/pages/shared/getFlatfileKey'

import {
  useFlatfile,
  useEvent,
  Workbook,
  Space,
  FlatfileProvider
 } from "@flatfile/react";
import { workbook } from "./configs/workbook";

const { useToken } = theme;

type Props = {
  buttonType: 'primary' | 'link',
  setImportAlertVisible: Dispatch<SetStateAction<boolean>>,
  setAlertDescription: Dispatch<SetStateAction<string>>,
};

const ImportFeedback: FC<Props> = ({ buttonType, setImportAlertVisible, setAlertDescription }) => {
  const user = useUserContext();

  if (!user) return null;

  const [publishableKey, setPublishableKey] = useState()
  getFlatfileKey().then(key => setPublishableKey(key))

  return (
    <FlatfileProvider
      publishableKey={publishableKey}
      config={{ displayAsModal: true, mountElement: "no-radius-iframe" }}
    >
      <WorkbookConfig
        buttonType={buttonType}
        setImportAlertVisible={setImportAlertVisible}
        setAlertDescription={setAlertDescription}
      />
    </FlatfileProvider>
  );
}

const WorkbookConfig = ({ buttonType, setImportAlertVisible, setAlertDescription }) => {
  const user = useUserContext();
  const projectId = useProjectId();
  const { token } = useToken();

  const { open, openPortal, closePortal } = useFlatfile();

  const toggleButton = () => {
    if (!open) {
      openPortal();
    } else {
      closePortal();
    }
  };

  // This will close the Portal instance when you confirm the dialog after the Workbook onSubmit function runs
  useEvent(
    "job:outcome-acknowledged",
    {
      operation: "workbookSubmitAction",
      status: "complete",
    },
    async (event) => {
      console.log({ event });
      closePortal();
      message.success('We\'re processing your new feedback.  Check back in a couple minutes, and we\'ll email you if there are any errors.');
    }
  );
  
  const { mutateAsync } = useImportFeedbackMutation({});

  return (
    <div className="content">
      <Button 
        type={buttonType}
        style={{
          color: lnTokens.cosmos,
          backgroundColor: lnTokens.titan,
          borderColor: '#d9d9d9',
          borderWidth: `${token.lineWidth}px`,
          borderStyle: token.lineType,
          borderRadius: token.borderRadius,
          boxShadow: lnTokens.buttonShadow,
          padding: '4px 16px',
          cursor: 'pointer',
        }}
        onClick={toggleButton}
      >
        {open === true ? "Close" : "Import"}
      </Button>
      <Space
        config={{
          name: user?.id
        }}
      >
        <Workbook
          config={workbook}

          onRecordHooks={[
            [
              (record) => {
                let enteredCategories = record.get("ideaCategories") as string
                let categories;
                if (enteredCategories) {
                  if (enteredCategories.indexOf(',') > 0) {
                    categories = enteredCategories.split(',').map((category: string) => category.trim());
                  } else {
                    categories = [enteredCategories.trim()];
                  }
                }
                let enteredPublished = record.get("ideaPublished") as string
                let published;
                if (enteredPublished) {
                  published = (enteredPublished === 'true');
                }

                record.set("ideaCategories", categories)
                record.set("ideaPublished", published)
                return record;
              },
            ],
          ]}

          onSubmit={async ({sheet}) => {
            console.log("onSubmit ", { sheet });
            const { records } = await sheet.allData();
            
            const chunkSize = 400;
            let i, j;
            for (i = 0, j = records.length; i < j; i += chunkSize) {
              const temporary: BatchFeedback[] = records.slice(i, i + chunkSize).map((record) => {
                const data = record.values
                const formatted = {
                  affectedCustomerEmail: data.affectedCustomerEmail.value,
                  content: data.content.value,
                  createdAt: data.createdAt.value,
                  importance: data.importance.value?.first,
                  reaction: data.reaction.value?.first,
                  reporterEmail: data.reporterEmail.value,
                  internalNote: data.internalNote.value,
                  ideaName: data.ideaName.value,
                  ideaNotes: data.ideaNotes.value,
                  ideaCategories: data.ideaCategories.value,
                  ideaCreatedAt: data.ideaCreatedAt.value,
                  ideaPublished: data.ideaPublished.value,
                }; 
                return Object.fromEntries(Object.entries(formatted).filter(([_, value]) => value !== ''))
              })
              await mutateAsync({ projectId, feedbacks: temporary },
              )
            }
            setAlertDescription(`${records.length} pieces of feedback are being added to your LaunchNotes workspace. Please refresh the page if they’re not yet visible.`);
            //Add this when the summary email is finished
            //setAlertDescription(`${dataLength} pieces of feedback are being added to your LaunchNotes workspace. Please refresh the page if they’re not yet visible. A summary email will be sent to ${user.email} when the import is complete.`);
            setImportAlertVisible(true);
          }}
        />
      </Space>
    </div>
  );
};

export default ImportFeedback;
